import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import './SeaCatReal.css';
const SeaCatReal = () => {
    const canvasRef = useRef(null);
    const videosRef = useRef([]);
    const [isLoading, setIsLoading] = useState(true);
    // Single source of truth for navigation state
    const navigationState = useRef({
        videoIndex: 0,
        isReverse: false,
        isPlaying: false
    });
    // React state for UI updates (derived from navigationState)
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [playbackDirection, setPlaybackDirection] = useState('forward');
    const [isInReverseMode, setIsInReverseMode] = useState(false);
    const [textVisible, setTextVisible] = useState(true);
    // Function to update navigation state and sync UI
    const updateNavigation = (videoIndex, isReverse, isPlaying = false) => {
        console.log(`[NAV] Updating to video ${videoIndex}, reverse: ${isReverse}, playing: ${isPlaying}`);
        // Check if video or mode changed (need text transition)
        const videoChanged = navigationState.current.videoIndex !== videoIndex;
        const modeChanged = navigationState.current.isReverse !== isReverse;
        if (videoChanged || modeChanged) {
            // Fade out current text
            setTextVisible(false);
            // Wait for fade out, then update content and fade in
            setTimeout(() => {
                // Update single source of truth
                navigationState.current = { videoIndex, isReverse, isPlaying };
                // Sync React state for UI updates
                setCurrentVideoIndex(videoIndex);
                setIsInReverseMode(isReverse);
                setIsPlaying(isPlaying);
                setPlaybackDirection(isReverse ? 'reverse' : 'forward');
                setProgress((videoIndex / (videoSources.length - 1)) * 100);
                // Fade in new text
                setTimeout(() => setTextVisible(true), 50);
            }, 300); // 300ms fade out duration
        }
        else {
            // No content change, just update playing state
            navigationState.current = { videoIndex, isReverse, isPlaying };
            setIsPlaying(isPlaying);
        }
    };
    const animationFrameRef = useRef();
    const pendingVideoIndex = useRef(null);
    const offscreenCanvasRef = useRef(null);
    const displayVideoIndex = useRef(0); // What's actually being displayed
    const transitioningToIndex = useRef(null); // Video we're transitioning to
    const transitionStartTime = useRef(0);
    // Normal video sources
    // Always use blob storage for videos (works in both dev and production)
    const videoSources = [
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-2.webm', // intro 1
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-6.webm', // intro 2
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-10.webm', // intro 3
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-14.webm', // intro 4
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-18.webm', // intro 5
    ];
    // Pre-reversed video sources - also use blob storage
    const reversedVideoSources = [
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-2-ezgif.com-reverse-video.webm',
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-6-ezgif.com-reverse-video.webm',
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-10-ezgif.com-reverse-video.webm',
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-14-ezgif.com-reverse-video.webm',
        'https://blobaneesh.blob.core.windows.net/videos/seacat-video-18-ezgif.com-reverse-video.webm',
    ];
    // Scroll state
    const scrollState = useRef({
        lastScrollTime: Date.now(),
        scrollCooldown: false,
        accumulatedDelta: 0,
        threshold: 100 // Amount of scroll needed to trigger next video
    });
    useEffect(() => {
        // Prevent native scroll
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, []);
    // Play video forward function with debug (defined early for use in loadVideos)
    const playVideoForward = async (index) => {
        // If we're switching from a playing video (for skippable videos)
        if (isPlaying && currentVideoIndex >= 5) {
            const currentVideo = videosRef.current[currentVideoIndex];
            if (currentVideo) {
                currentVideo.pause();
                currentVideo.currentTime = 0;
            }
        }
        const video = videosRef.current[index];
        if (!video)
            return;
        console.log(`Playing video ${index} forward`);
        // Set cooldown only for intro videos (0-4), but clear it after 2 seconds to allow scrolling
        if (index < 5) {
            scrollState.current.scrollCooldown = true;
            setTimeout(() => {
                scrollState.current.scrollCooldown = false;
            }, 2000);
        }
        else {
            scrollState.current.scrollCooldown = false;
        }
        setIsPlaying(true);
        setProgress((index / (videoSources.length - 1)) * 100);
        setPlaybackDirection('forward');
        // Reset and prepare video
        video.currentTime = 0;
        video.playbackRate = 1;
        // Ensure video is ready before switching
        if (video.readyState < 2) {
            await new Promise((resolve) => {
                const handleCanPlay = () => {
                    video.removeEventListener('canplay', handleCanPlay);
                    resolve();
                };
                video.addEventListener('canplay', handleCanPlay);
            });
        }
        // Now switch to the new video
        setCurrentVideoIndex(index);
        try {
            await video.play();
            // Add performance monitoring for forward playback
            let forwardFrameCount = 0;
            const forwardFrameMonitor = () => {
                if (!video.paused && !video.ended) {
                    forwardFrameCount++;
                    if (forwardFrameCount % 30 === 0) { // Every 30 frames (about 1 second at 30fps)
                    }
                    requestAnimationFrame(forwardFrameMonitor);
                }
            };
            forwardFrameMonitor();
        }
        catch (e) {
            console.error('Play failed:', e);
            setIsPlaying(false);
            scrollState.current.scrollCooldown = false;
        }
    };
    // Load all videos
    useEffect(() => {
        const loadVideos = async () => {
            const videos = [];
            for (const src of videoSources) {
                const video = document.createElement('video');
                video.src = src;
                video.muted = true;
                video.playsInline = true;
                video.preload = 'auto';
                // MUST set crossOrigin for canvas drawing to work
                video.crossOrigin = 'anonymous';
                // Hide video elements
                video.style.position = 'fixed';
                video.style.opacity = '0.001';
                video.style.pointerEvents = 'none';
                video.style.left = '-9999px';
                document.body.appendChild(video);
                await new Promise((resolve) => {
                    video.addEventListener('loadeddata', () => resolve(), { once: true });
                    video.addEventListener('error', () => {
                        console.error(`Failed to load: ${src}`);
                        resolve();
                    }, { once: true });
                    video.load();
                });
                // Prebuffer the end of the video for smooth reverse playback
                if (video.duration && video.duration > 1) {
                    // Seek to near the end to buffer that portion
                    await new Promise((resolve) => {
                        const handleSeeked = () => {
                            video.removeEventListener('seeked', handleSeeked);
                            // Seek back to start
                            video.currentTime = 0;
                            resolve();
                        };
                        video.addEventListener('seeked', handleSeeked);
                        video.currentTime = video.duration - 0.5;
                    });
                }
                // Add video event listeners (only update state if this is the active video)
                video.addEventListener('play', () => {
                    const videoIndex = videosRef.current.indexOf(video);
                    if (videoIndex === navigationState.current.videoIndex) {
                        navigationState.current.isPlaying = true;
                        setIsPlaying(true);
                    }
                });
                video.addEventListener('pause', () => {
                    const videoIndex = videosRef.current.indexOf(video);
                    if (videoIndex === navigationState.current.videoIndex) {
                        navigationState.current.isPlaying = false;
                        setIsPlaying(false);
                    }
                });
                video.addEventListener('ended', () => {
                    const videoIndex = videosRef.current.indexOf(video);
                    if (videoIndex === navigationState.current.videoIndex) {
                        navigationState.current.isPlaying = false;
                        setIsPlaying(false);
                        scrollState.current.scrollCooldown = false;
                        console.log(`Video ended`);
                    }
                });
                videos.push(video);
            }
            // Preload all reversed videos to prevent blinking
            console.log('Preloading reversed videos...');
            for (let i = 0; i < reversedVideoSources.length; i++) {
                const reversedSrc = reversedVideoSources[i];
                if (reversedSrc) {
                    const tempVideo = document.createElement('video');
                    tempVideo.src = reversedSrc;
                    tempVideo.muted = true;
                    tempVideo.playsInline = true;
                    tempVideo.preload = 'auto';
                    tempVideo.crossOrigin = 'anonymous';
                    // Hide temp video
                    tempVideo.style.position = 'fixed';
                    tempVideo.style.opacity = '0.001';
                    tempVideo.style.pointerEvents = 'none';
                    tempVideo.style.left = '-9999px';
                    document.body.appendChild(tempVideo);
                    await new Promise((resolve) => {
                        tempVideo.addEventListener('loadeddata', () => {
                            console.log(`Preloaded reversed video ${i}: ${reversedSrc}`);
                            tempVideo.remove(); // Remove temp video after preloading
                            resolve();
                        }, { once: true });
                        tempVideo.addEventListener('error', () => {
                            console.error(`Failed to preload reversed: ${reversedSrc}`);
                            tempVideo.remove();
                            resolve();
                        }, { once: true });
                        tempVideo.load();
                    });
                }
            }
            console.log('All reversed videos preloaded!');
            videosRef.current = videos;
            setIsLoading(false);
            // Auto-play the first introduction video
            if (videos.length > 0) {
                setTimeout(() => {
                    const video = videosRef.current[0];
                    const normalVideoSrc = videoSources[0];
                    if (video && normalVideoSrc) {
                        video.src = normalVideoSrc;
                        video.currentTime = 0;
                        updateNavigation(0, false, false);
                        const playWhenReady = () => {
                            if (video.readyState >= 2) {
                                video.play().catch(console.error);
                            }
                            else {
                                video.addEventListener('loadeddata', () => {
                                    video.play().catch(console.error);
                                }, { once: true });
                            }
                        };
                        playWhenReady();
                    }
                }, 500);
            }
        };
        loadVideos();
        return () => {
            videosRef.current.forEach(v => v.remove());
        };
    }, []);
    // Main render loop
    useEffect(() => {
        if (isLoading)
            return;
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d', {
            alpha: false,
            desynchronized: true
        });
        if (!ctx)
            return;
        // Create offscreen canvas for double buffering
        if (!offscreenCanvasRef.current) {
            offscreenCanvasRef.current = document.createElement('canvas');
        }
        const offscreenCanvas = offscreenCanvasRef.current;
        const offscreenCtx = offscreenCanvas.getContext('2d', {
            alpha: false,
            desynchronized: true
        });
        if (!offscreenCtx)
            return;
        // Set canvas size
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            offscreenCanvas.width = window.innerWidth;
            offscreenCanvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        // Handle wheel events
        const handleWheel = (e) => {
            e.preventDefault();
            // Check if any video is currently playing
            const currentVideo = videosRef.current[navigationState.current.videoIndex];
            if (currentVideo && !currentVideo.paused && !currentVideo.ended) {
                console.log('[WHEEL] Video is playing, blocking scroll navigation');
                return;
            }
            // Don't process during a transition to prevent conflicts
            if (displayVideoIndex.current !== currentVideoIndex) {
                console.log('[WHEEL] Ignoring wheel during transition');
                return;
            }
            // Check if we're transitioning (using transitioningToIndex as well)
            if (transitioningToIndex.current !== null) {
                console.log('[WHEEL] Ignoring wheel - transition in progress');
                return;
            }
            // For videos 5+ (Operation Mode onwards), allow skipping immediately
            // Don't process if in cooldown (only for intro videos)
            if (scrollState.current.scrollCooldown && currentVideoIndex < 5)
                return;
            // Accumulate scroll
            scrollState.current.accumulatedDelta += Math.abs(e.deltaY);
            // Check if we've scrolled enough
            if (scrollState.current.accumulatedDelta >= scrollState.current.threshold) {
                const direction = e.deltaY > 0 ? 1 : -1;
                // Reset accumulator
                scrollState.current.accumulatedDelta = 0;
                // Determine next video
                let nextIndex = currentVideoIndex;
                if (direction > 0) {
                    // Wheel down = move forward through normal videos, then reverse videos
                    console.log(`Wheel DOWN: videoIndex=${navigationState.current.videoIndex}, isInReverseMode=${navigationState.current.isReverse}`);
                    if (!navigationState.current.isReverse && navigationState.current.videoIndex < videoSources.length - 1) {
                        // If we're in normal mode and not at the last video, go to next video
                        const nextIndex = navigationState.current.videoIndex + 1;
                        const normalVideoSrc = videoSources[nextIndex];
                        const video = videosRef.current[nextIndex];
                        if (video && normalVideoSrc) {
                            // Pause current video
                            const currentVideo = videosRef.current[navigationState.current.videoIndex];
                            if (currentVideo) {
                                currentVideo.pause();
                            }
                            // The video element should already have the correct src from loadVideos
                            video.currentTime = 0;
                            updateNavigation(nextIndex, false, false);
                            // Video should already be loaded, play immediately
                            video.play().catch(console.error);
                        }
                    }
                    else if (!navigationState.current.isReverse && navigationState.current.videoIndex === videoSources.length - 1) {
                        // At the last normal video, switch to reverse mode of LAST video
                        const videoIndex = navigationState.current.videoIndex;
                        console.log(`At last video, switching to REVERSE mode on video ${videoIndex}`);
                        const reversedVideoSrc = reversedVideoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && reversedVideoSrc) {
                            video.pause();
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            // Update navigation state BEFORE playing
                            updateNavigation(videoIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (navigationState.current.isReverse && navigationState.current.videoIndex > 0) {
                        // In reverse mode, continue to previous video in reverse
                        const prevIndex = navigationState.current.videoIndex - 1;
                        console.log(`Going from video ${navigationState.current.videoIndex} REVERSE to video ${prevIndex} REVERSE`);
                        const reversedVideoSrc = reversedVideoSources[prevIndex];
                        const video = videosRef.current[prevIndex];
                        if (video && reversedVideoSrc) {
                            // Stop current video
                            const currentVideo = videosRef.current[navigationState.current.videoIndex];
                            if (currentVideo) {
                                currentVideo.pause();
                            }
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(prevIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                }
                else if (direction < 0) {
                    // Wheel up = go backward
                    console.log(`Wheel UP: videoIndex=${navigationState.current.videoIndex}, isInReverseMode=${navigationState.current.isReverse}`);
                    if (navigationState.current.isReverse) {
                        // If we're in reverse mode, go back to normal mode of current video
                        const videoIndex = navigationState.current.videoIndex;
                        console.log(`Going from REVERSE to NORMAL on video ${videoIndex}`);
                        const normalVideoSrc = videoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && normalVideoSrc) {
                            video.pause();
                            video.src = normalVideoSrc;
                            video.currentTime = 0;
                            // Update navigation state BEFORE playing
                            updateNavigation(videoIndex, false, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (!navigationState.current.isReverse && navigationState.current.videoIndex > 0) {
                        // If we're in normal mode and not at first video, switch to reverse mode
                        const videoIndex = navigationState.current.videoIndex;
                        console.log(`Going from NORMAL to REVERSE on video ${videoIndex}`);
                        const reversedVideoSrc = reversedVideoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && reversedVideoSrc) {
                            video.pause();
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            // Update navigation state BEFORE playing
                            updateNavigation(videoIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (!navigationState.current.isReverse && navigationState.current.videoIndex === 0) {
                        // At first video in normal mode, switch to reverse
                        const videoIndex = 0;
                        console.log(`At first video, switching to REVERSE mode`);
                        const reversedVideoSrc = reversedVideoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && reversedVideoSrc) {
                            video.pause();
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(videoIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (navigationState.current.isReverse && navigationState.current.videoIndex < videoSources.length - 1) {
                        // In reverse mode, go to next video in reverse mode
                        const nextIndex = navigationState.current.videoIndex + 1;
                        console.log(`Going from video ${navigationState.current.videoIndex} REVERSE to video ${nextIndex} REVERSE`);
                        const reversedVideoSrc = reversedVideoSources[nextIndex];
                        const video = videosRef.current[nextIndex];
                        if (video && reversedVideoSrc) {
                            // Stop current video
                            const currentVideo = videosRef.current[navigationState.current.videoIndex];
                            if (currentVideo) {
                                currentVideo.pause();
                            }
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(nextIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                }
            }
        };
        // Play video in reverse
        const playVideoReverse = async (index) => {
            // Don't start reverse if we're transitioning
            if (transitioningToIndex.current !== null || displayVideoIndex.current !== currentVideoIndex) {
                return;
            }
            // Stop current video if playing
            if (isPlaying) {
                const currentVideo = videosRef.current[currentVideoIndex];
                if (currentVideo) {
                    currentVideo.pause();
                }
                // Cancel any existing animation frame
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
            }
            const video = videosRef.current[index];
            if (!video) {
                console.error(`[REVERSE DEBUG] No video found at index ${index}`);
                return;
            }
            setIsPlaying(true);
            setProgress((index / (videoSources.length - 1)) * 100);
            setPlaybackDirection('reverse');
            scrollState.current.scrollCooldown = index < 5;
            // Ensure video is loaded and ready to play
            const startReversePlayback = async () => {
                console.log(`Video ${index} state - readyState: ${video.readyState}, duration: ${video.duration}, currentTime: ${video.currentTime}`);
                // First ensure we have the video loaded and ready
                if (video.readyState < 4) { // HAVE_ENOUGH_DATA
                    // Pre-seek to buffer the end portion
                    video.currentTime = video.duration - 0.5;
                    await new Promise((resolve) => {
                        const handleCanPlayThrough = () => {
                            video.removeEventListener('canplaythrough', handleCanPlayThrough);
                            resolve();
                        };
                        if (video.readyState >= 4) {
                            resolve();
                        }
                        else {
                            video.addEventListener('canplaythrough', handleCanPlayThrough);
                            // Start loading the video
                            video.load();
                        }
                    });
                }
                else {
                }
                // Seek to near the end (WebM needs more buffer time)
                const targetTime = video.duration - 0.2;
                // Longer delay for WebM seeking
                await new Promise(resolve => setTimeout(resolve, 100));
                // Use a promise to wait for the seek to complete
                await new Promise((resolve) => {
                    const handleSeeked = () => {
                        video.removeEventListener('seeked', handleSeeked);
                        resolve();
                    };
                    video.addEventListener('seeked', handleSeeked);
                    video.currentTime = targetTime;
                });
                // Longer delay after seeking for WebM stabilization
                await new Promise(resolve => setTimeout(resolve, 150));
                // For WebM, don't wait - just switch immediately since drawability is unreliable
                setCurrentVideoIndex(index);
                // Manually animate reverse playback
                let lastTimestamp = performance.now();
                const reversePlayback = (timestamp) => {
                    const frameTimestamp = timestamp || performance.now();
                    if (!video || video.currentTime <= 0.05) {
                        setIsPlaying(false);
                        scrollState.current.scrollCooldown = false;
                        // After finishing reverse playback, move to previous video
                        if (index > 0) {
                            const prevIndex = index - 1;
                            const prevVideo = videosRef.current[prevIndex];
                            if (prevVideo) {
                                // DON'T SEEK! The video should already be at its end from when it played forward
                                // Seeking causes readyState to drop from 4 to 1, causing the blink!
                                // Add much longer delay to let BOTH videos stabilize
                                setTimeout(() => {
                                    // Wait for the current video (that just finished reverse) to stabilize
                                    const currentVideo = videosRef.current[index];
                                    const waitForPrevReady = () => {
                                        // Check if the target video can actually be drawn
                                        const canDrawPrev = () => {
                                            if (!prevVideo || prevVideo.readyState < 2)
                                                return false;
                                            if (!prevVideo.videoWidth || !prevVideo.videoHeight)
                                                return false;
                                            try {
                                                const testCanvas = document.createElement('canvas');
                                                testCanvas.width = 1;
                                                testCanvas.height = 1;
                                                const testCtx = testCanvas.getContext('2d');
                                                if (!testCtx)
                                                    return false;
                                                testCtx.drawImage(prevVideo, 0, 0, 1, 1);
                                                return true;
                                            }
                                            catch (e) {
                                                console.error(`[REVERSE DEBUG] Cannot draw prev video ${prevIndex}:`, e);
                                                return false;
                                            }
                                        };
                                        if (canDrawPrev()) {
                                            // DON'T switch immediately - let the transition handle it
                                            // Just update the target index and let the fade transition occur
                                            setCurrentVideoIndex(prevIndex);
                                            setProgress((prevIndex / (videoSources.length - 1)) * 100);
                                        }
                                        else {
                                            setTimeout(waitForPrevReady, 50);
                                        }
                                    };
                                    waitForPrevReady();
                                }, 300); // Much longer initial delay
                            }
                        }
                        return;
                    }
                    // Calculate time delta since last frame
                    const deltaTime = (frameTimestamp - lastTimestamp) / 1000; // Convert to seconds
                    lastTimestamp = frameTimestamp;
                    // Step back by the actual time that has passed (normal playback speed)
                    video.currentTime = Math.max(0, video.currentTime - deltaTime);
                    // Don't cache during reverse - it causes glitches
                    // The existing frame cache from forward playback should be enough
                    if (video.currentTime > 0.05) {
                        animationFrameRef.current = requestAnimationFrame(reversePlayback);
                    }
                    else {
                        setIsPlaying(false);
                        scrollState.current.scrollCooldown = false;
                        // After finishing reverse playback, move to previous video
                        if (index > 0) {
                            const prevIndex = index - 1;
                            const prevVideo = videosRef.current[prevIndex];
                            console.log(`Transitioning from video ${index} to ${prevIndex}`);
                            if (prevVideo) {
                                // DON'T SEEK! The video should already be at its end from when it played forward
                                // Seeking causes readyState to drop from 4 to 1, causing the blink!
                                // Add much longer delay to let BOTH videos stabilize
                                setTimeout(() => {
                                    // Wait for the current video (that just finished reverse) to stabilize
                                    const currentVideo = videosRef.current[index];
                                    const waitForPrevReady = () => {
                                        // Check if the target video can actually be drawn
                                        const canDrawPrev = () => {
                                            if (!prevVideo || prevVideo.readyState < 2)
                                                return false;
                                            if (!prevVideo.videoWidth || !prevVideo.videoHeight)
                                                return false;
                                            try {
                                                const testCanvas = document.createElement('canvas');
                                                testCanvas.width = 1;
                                                testCanvas.height = 1;
                                                const testCtx = testCanvas.getContext('2d');
                                                if (!testCtx)
                                                    return false;
                                                testCtx.drawImage(prevVideo, 0, 0, 1, 1);
                                                return true;
                                            }
                                            catch (e) {
                                                return false;
                                            }
                                        };
                                        if (canDrawPrev()) {
                                            // DON'T switch immediately - let the transition handle it
                                            // Just update the target index and let the fade transition occur
                                            setCurrentVideoIndex(prevIndex);
                                            setProgress((prevIndex / (videoSources.length - 1)) * 100);
                                        }
                                        else {
                                            setTimeout(waitForPrevReady, 50);
                                        }
                                    };
                                    waitForPrevReady();
                                }, 300); // Much longer initial delay
                            }
                        }
                    }
                };
                // Start reverse playback animation
                reversePlayback();
            };
            startReversePlayback();
        };
        // Try SIMPLE video reverse methods first
        const playVideoReverseWebM = async (index) => {
            console.log(`Playing reversed video ${index}`);
            // Simply switch to the pre-reversed video
            const reversedVideoSrc = reversedVideoSources[index];
            if (!reversedVideoSrc)
                return;
            // Stop current video
            const currentVideo = videosRef.current[currentVideoIndex];
            if (currentVideo) {
                currentVideo.pause();
            }
            // Switch the current video to the reversed source
            if (currentVideo) {
                currentVideo.src = reversedVideoSrc;
                currentVideo.currentTime = 0;
                setPlaybackDirection('reverse');
                setIsPlaying(true);
                try {
                    await currentVideo.play();
                    console.log(`Started playing reversed video ${index}`);
                }
                catch (error) {
                    console.error(`Failed to play reversed video ${index}:`, error);
                    setIsPlaying(false);
                }
            }
        };
        window.addEventListener('wheel', handleWheel, { passive: false });
        // Touch handling
        let touchStartY = 0;
        let touchAccumulated = 0;
        const handleTouchStart = (e) => {
            touchStartY = e.touches[0].clientY;
            touchAccumulated = 0;
        };
        const handleTouchMove = (e) => {
            e.preventDefault();
            // Check if any video is currently playing
            const currentVideo = videosRef.current[navigationState.current.videoIndex];
            if (currentVideo && !currentVideo.paused && !currentVideo.ended) {
                console.log('[TOUCH] Video is playing, blocking touch navigation');
                return;
            }
            const deltaY = touchStartY - e.touches[0].clientY;
            touchAccumulated += Math.abs(deltaY);
            if (touchAccumulated > 50) { // Touch threshold
                const direction = deltaY > 0 ? 1 : -1;
                // Use same logic as handleWheel
                if (direction > 0) {
                    // Swipe up = move forward through normal videos, then reverse videos
                    console.log(`Touch UP (forward): videoIndex=${navigationState.current.videoIndex}, isInReverseMode=${navigationState.current.isReverse}`);
                    if (!navigationState.current.isReverse && navigationState.current.videoIndex < videoSources.length - 1) {
                        // If we're in normal mode and not at the last video, go to next video
                        const nextIndex = navigationState.current.videoIndex + 1;
                        const video = videosRef.current[nextIndex];
                        if (video) {
                            // Pause current video
                            const currentVideo = videosRef.current[navigationState.current.videoIndex];
                            if (currentVideo) {
                                currentVideo.pause();
                            }
                            video.currentTime = 0;
                            updateNavigation(nextIndex, false, false);
                            video.play().catch(console.error);
                        }
                    }
                    else if (!navigationState.current.isReverse && navigationState.current.videoIndex === videoSources.length - 1) {
                        // At the last normal video, switch to reverse mode of LAST video
                        const videoIndex = navigationState.current.videoIndex;
                        console.log(`At last video, switching to REVERSE mode on video ${videoIndex}`);
                        const reversedVideoSrc = reversedVideoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && reversedVideoSrc) {
                            video.pause();
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(videoIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (navigationState.current.isReverse && navigationState.current.videoIndex > 0) {
                        // In reverse mode, continue to previous video in reverse
                        const prevIndex = navigationState.current.videoIndex - 1;
                        console.log(`Going from video ${navigationState.current.videoIndex} REVERSE to video ${prevIndex} REVERSE`);
                        const reversedVideoSrc = reversedVideoSources[prevIndex];
                        const video = videosRef.current[prevIndex];
                        if (video && reversedVideoSrc) {
                            // Stop current video
                            const currentVideo = videosRef.current[navigationState.current.videoIndex];
                            if (currentVideo) {
                                currentVideo.pause();
                            }
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(prevIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                }
                else if (direction < 0) {
                    // Swipe down = go backward
                    console.log(`Touch DOWN (backward): videoIndex=${navigationState.current.videoIndex}, isInReverseMode=${navigationState.current.isReverse}`);
                    if (navigationState.current.isReverse) {
                        // If we're in reverse mode, go back to normal mode of current video
                        const videoIndex = navigationState.current.videoIndex;
                        console.log(`Going from REVERSE to NORMAL on video ${videoIndex}`);
                        const normalVideoSrc = videoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && normalVideoSrc) {
                            video.pause();
                            video.src = normalVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(videoIndex, false, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (!navigationState.current.isReverse && navigationState.current.videoIndex > 0) {
                        // If we're in normal mode and not at first video, switch to reverse mode
                        const videoIndex = navigationState.current.videoIndex;
                        console.log(`Going from NORMAL to REVERSE on video ${videoIndex}`);
                        const reversedVideoSrc = reversedVideoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && reversedVideoSrc) {
                            video.pause();
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(videoIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (!navigationState.current.isReverse && navigationState.current.videoIndex === 0) {
                        // At first video in normal mode, switch to reverse
                        const videoIndex = 0;
                        console.log(`At first video, switching to REVERSE mode`);
                        const reversedVideoSrc = reversedVideoSources[videoIndex];
                        const video = videosRef.current[videoIndex];
                        if (video && reversedVideoSrc) {
                            video.pause();
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(videoIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                    else if (navigationState.current.isReverse && navigationState.current.videoIndex < videoSources.length - 1) {
                        // In reverse mode, go to next video in reverse mode
                        const nextIndex = navigationState.current.videoIndex + 1;
                        console.log(`Going from video ${navigationState.current.videoIndex} REVERSE to video ${nextIndex} REVERSE`);
                        const reversedVideoSrc = reversedVideoSources[nextIndex];
                        const video = videosRef.current[nextIndex];
                        if (video && reversedVideoSrc) {
                            // Stop current video
                            const currentVideo = videosRef.current[navigationState.current.videoIndex];
                            if (currentVideo) {
                                currentVideo.pause();
                            }
                            video.src = reversedVideoSrc;
                            video.currentTime = 0;
                            updateNavigation(nextIndex, true, true);
                            const playWhenReady = () => {
                                if (video.readyState >= 2) {
                                    video.play().catch(console.error);
                                }
                                else {
                                    video.addEventListener('loadeddata', () => {
                                        video.play().catch(console.error);
                                    }, { once: true });
                                }
                            };
                            playWhenReady();
                        }
                    }
                }
                touchAccumulated = 0;
            }
            touchStartY = e.touches[0].clientY;
        };
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        // Animation loop
        let animationId;
        let lastDrawnFrame = false;
        let lastVideoIndex = -1;
        const drawVideo = (video, opacity = 1, label = '') => {
            if (!video) {
                console.warn(`[DRAW ${label}] No video object`);
                return false;
            }
            if (video.readyState < 2) {
                // Don't log every frame - this is expected during loading
                return false;
            }
            if (!video.videoWidth || !video.videoHeight) {
                console.warn(`[DRAW ${label}] Invalid dimensions: ${video.videoWidth}x${video.videoHeight}`);
                return false;
            }
            const videoAspect = video.videoWidth / video.videoHeight;
            const canvasAspect = canvas.width / canvas.height;
            let dw = canvas.width;
            let dh = canvas.height;
            let dx = 0;
            let dy = 0;
            if (videoAspect > canvasAspect) {
                dh = canvas.height;
                dw = dh * videoAspect;
                dx = (canvas.width - dw) / 2;
            }
            else {
                dw = canvas.width;
                dh = dw / videoAspect;
                dy = (canvas.height - dh) / 2;
            }
            if (opacity < 1) {
                ctx.globalAlpha = opacity;
            }
            try {
                ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, dx, dy, dw, dh);
                // console.log(`[DRAW ${label}] Success at time ${video.currentTime}`);
            }
            catch (e) {
                // For WebM during reverse playback, drawing can fail temporarily - this is expected
                if (video.src.includes('.webm')) {
                    // Don't log errors for WebM - just indicate drawing failed
                    return false;
                }
                else {
                    console.error(`[DRAW ${label}] Failed to draw:`, e);
                    return false;
                }
            }
            if (opacity < 1) {
                ctx.globalAlpha = 1;
            }
            return true;
        };
        const animate = () => {
            animationId = requestAnimationFrame(animate);
            // Start transition if needed
            if (displayVideoIndex.current !== currentVideoIndex && transitioningToIndex.current === null) {
                const targetVideo = videosRef.current[currentVideoIndex];
                // For WebM, be more lenient with transition requirements
                if (targetVideo && ((targetVideo.readyState >= 2 && targetVideo.videoWidth > 0 && targetVideo.videoHeight > 0) || targetVideo.src.includes('.webm'))) {
                    transitioningToIndex.current = currentVideoIndex;
                    transitionStartTime.current = performance.now();
                }
                else {
                }
            }
            // Handle transition
            if (transitioningToIndex.current !== null) {
                const elapsed = performance.now() - transitionStartTime.current;
                const transitionDuration = 100; // 100ms transition
                // Check if both videos are ready for transition
                const oldVideo = videosRef.current[displayVideoIndex.current];
                const newVideo = videosRef.current[transitioningToIndex.current];
                // Test if videos can actually be drawn (not just readyState)
                const canDrawOld = () => {
                    if (!oldVideo || oldVideo.readyState < 2)
                        return false;
                    if (!oldVideo.videoWidth || !oldVideo.videoHeight)
                        return false;
                    try {
                        // Create a small test canvas to verify drawing works
                        const testCanvas = document.createElement('canvas');
                        testCanvas.width = 1;
                        testCanvas.height = 1;
                        const testCtx = testCanvas.getContext('2d');
                        if (!testCtx)
                            return false;
                        testCtx.drawImage(oldVideo, 0, 0, 1, 1);
                        return true;
                    }
                    catch (e) {
                        return false;
                    }
                };
                const canDrawNew = () => {
                    if (!newVideo || newVideo.readyState < 2)
                        return false;
                    if (!newVideo.videoWidth || !newVideo.videoHeight)
                        return false;
                    try {
                        const testCanvas = document.createElement('canvas');
                        testCanvas.width = 1;
                        testCanvas.height = 1;
                        const testCtx = testCanvas.getContext('2d');
                        if (!testCtx)
                            return false;
                        testCtx.drawImage(newVideo, 0, 0, 1, 1);
                        return true;
                    }
                    catch (e) {
                        return false;
                    }
                };
                const oldReady = canDrawOld();
                const newReady = canDrawNew();
                // Only log when there's an issue
                if (!oldReady || !newReady) {
                }
                // Special case: if old video not ready but new video is, just switch immediately
                if (!oldReady && newReady) {
                    displayVideoIndex.current = transitioningToIndex.current;
                    transitioningToIndex.current = null;
                    // Clear and draw the new video immediately
                    ctx.fillStyle = '#000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    drawVideo(newVideo, 1, `DIRECT-${displayVideoIndex.current}`);
                    return;
                }
                // If neither ready, wait
                if (!oldReady && !newReady) {
                    transitionStartTime.current = performance.now();
                    return; // Don't proceed with transition
                }
                // If only old ready but not new, keep showing old
                if (oldReady && !newReady) {
                    transitionStartTime.current = performance.now();
                    drawVideo(oldVideo, 1, `WAITING-${displayVideoIndex.current}`);
                    return;
                }
                if (elapsed >= transitionDuration) {
                    // Transition complete
                    displayVideoIndex.current = transitioningToIndex.current;
                    transitioningToIndex.current = null;
                    // Draw the new video
                    const video = videosRef.current[displayVideoIndex.current];
                    if (!drawVideo(video)) {
                        // Fallback - keep last frame
                    }
                }
                else {
                    // Mid-transition - cross-fade
                    const progress = elapsed / transitionDuration;
                    // DON'T clear canvas during transition!
                    // Only log first and last progress
                    if (progress < 0.05 || progress > 0.95) {
                    }
                    // Draw old video first (fading out)
                    ctx.save();
                    ctx.globalAlpha = 1 - progress;
                    const oldDrawn = drawVideo(oldVideo, 1, `OLD-${displayVideoIndex.current}`);
                    ctx.restore();
                    if (!oldDrawn) {
                    }
                    // Draw new video on top (fading in)
                    ctx.save();
                    ctx.globalAlpha = progress;
                    const newDrawn = drawVideo(newVideo, 1, `NEW-${transitioningToIndex.current}`);
                    ctx.restore();
                    if (!newDrawn) {
                    }
                }
            }
            else {
                // No transition - just draw current video
                const video = videosRef.current[currentVideoIndex];
                const drawn = drawVideo(video, 1, `VIDEO-${currentVideoIndex}`);
                if (drawn) {
                    lastDrawnFrame = true;
                    lastVideoIndex = displayVideoIndex.current;
                }
                else if (lastVideoIndex === displayVideoIndex.current && lastDrawnFrame) {
                    // Same video that was drawable before - just keep showing last frame
                    // This prevents blink when video temporarily becomes undrawable
                    // Don't log this - it happens frequently and is expected
                }
                else if (!lastDrawnFrame) {
                    // Only clear canvas if we've never drawn anything
                    ctx.fillStyle = '#000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }
                // If draw failed but we have a last frame, just keep showing it
            }
        };
        animate();
        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [isLoading, currentVideoIndex, isPlaying]);
    return (_jsxs("div", { className: "seacat-real", children: [_jsx("canvas", { ref: canvasRef, style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: '#000'
                } }), isLoading && (_jsx("div", { className: "loading-overlay", children: _jsxs("div", { className: "loading-content", children: [_jsx("h2", { children: "Loading Experience" }), _jsx("div", { className: "loading-spinner" })] }) })), !isLoading && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "diamond-vision-overlay", children: [_jsxs("div", { className: `vision-text ${textVisible ? 'visible' : 'hidden'}`, children: [currentVideoIndex === 0 && !isInReverseMode && (_jsxs("div", { className: "text-stage stage-1", children: [_jsx("h1", { className: "primary-text", children: "Imagine stepping into" }), _jsx("h2", { className: "secondary-text", children: "your showroom... virtually" }), _jsx("div", { className: "accent-line" }), _jsx("p", { className: "subtitle", children: "Every angle, every sparkle, captured in motion" })] })), currentVideoIndex === 0 && isInReverseMode && (_jsxs("div", { className: "text-stage stage-1-reverse", children: [_jsx("h1", { className: "primary-text", children: "Your customers," }), _jsx("h2", { className: "secondary-text", children: "immersed before they arrive" }), _jsx("div", { className: "accent-line" })] })), currentVideoIndex === 1 && !isInReverseMode && (_jsxs("div", { className: "text-stage stage-2", children: [_jsx("h1", { className: "primary-text", children: "Navigate your space" }), _jsx("h2", { className: "secondary-text", children: "like never before" }), _jsx("div", { className: "accent-line" }), _jsx("p", { className: "subtitle", children: "Scroll through your entire collection" })] })), currentVideoIndex === 1 && isInReverseMode && (_jsxs("div", { className: "text-stage stage-2-reverse", children: [_jsx("h1", { className: "primary-text", children: "Move between displays" }), _jsx("h2", { className: "secondary-text", children: "with a gesture" }), _jsx("div", { className: "accent-line" })] })), currentVideoIndex === 2 && !isInReverseMode && (_jsxs("div", { className: "text-stage stage-3", children: [_jsx("h1", { className: "primary-text", children: "Interactive showcases" }), _jsx("h2", { className: "secondary-text", children: "at your fingertips" }), _jsx("div", { className: "accent-line" }), _jsx("p", { className: "subtitle", children: "Click to explore any piece" })] })), currentVideoIndex === 2 && isInReverseMode && (_jsxs("div", { className: "text-stage stage-3-reverse", children: [_jsx("h1", { className: "primary-text", children: "Hey Aneesh," }), _jsx("h2", { className: "secondary-text", children: "still scrolling?" }), _jsx("div", { className: "accent-line" }), _jsx("p", { className: "subtitle", children: "Your showroom would look amazing like this, wouldn't it?" })] })), currentVideoIndex === 3 && !isInReverseMode && (_jsxs("div", { className: "text-stage stage-4", children: [_jsx("h1", { className: "primary-text", children: "Walk through your store" }), _jsx("h2", { className: "secondary-text", children: "from anywhere" }), _jsx("div", { className: "accent-line" }), _jsx("p", { className: "subtitle", children: "360\u00B0 exploration of your finest pieces" })] })), currentVideoIndex === 3 && isInReverseMode && (_jsxs("div", { className: "text-stage stage-4-reverse", children: [_jsx("h1", { className: "primary-text", children: "Customers browse" }), _jsx("h2", { className: "secondary-text", children: "as if they're there" }), _jsx("div", { className: "accent-line" })] })), currentVideoIndex === 4 && !isInReverseMode && (_jsxs("div", { className: "text-stage stage-5", children: [_jsx("h1", { className: "primary-text", children: "Luxury retail," }), _jsx("h2", { className: "secondary-text", children: "redefined" }), _jsx("div", { className: "accent-line" }), _jsx("p", { className: "subtitle", children: "Turn every screen into your storefront" })] })), currentVideoIndex === 4 && isInReverseMode && (_jsxs("div", { className: "text-stage stage-5-reverse", children: [_jsx("h1", { className: "primary-text", children: "Every diamond" }), _jsx("h2", { className: "secondary-text", children: "tells a story in motion" }), _jsx("div", { className: "accent-line" })] }))] }), _jsx("div", { className: "diamond-particles", children: [...Array(12)].map((_, i) => (_jsx("div", { className: "particle", style: {
                                        '--delay': `${i * 0.5}s`,
                                        '--duration': `${4 + (i % 3)}s`,
                                        '--x': `${20 + (i * 7)}%`,
                                        '--y': `${10 + (i * 8)}%`
                                    } }, i))) })] }), _jsx("div", { className: "simple-progress", children: videoSources.map((_, index) => (_jsx("div", { className: `simple-dot ${index === currentVideoIndex && !isInReverseMode ? 'active-normal' :
                                index === currentVideoIndex && isInReverseMode ? 'active-reverse' :
                                    index < currentVideoIndex ? 'completed' : 'pending'}` }, index))) }), _jsx("div", { className: "status-indicator", children: isPlaying ? (currentVideoIndex >= 5 ? (_jsx("span", { className: "playing", children: "Playing (scroll to skip)" })) : (_jsx("span", { className: "playing", children: "Playing..." }))) : (_jsx("span", { className: "ready", children: "Scroll to continue" })) })] }))] }));
};
export default SeaCatReal;
